import { useSWRWithAuth } from "@today/api"
import { Client } from "@today/api/taker"
import { useAuthentication, useUserInfo } from "@today/auth"
import { useRouter } from "next/router"
import { usePoints } from "../providers/PointsProvider"
import { FASTBOX_CLIENT_IDS } from "../components/dev-navigation/clientIds.store"
import { ORDERS_HIDDEN_CLIENT_IDS } from "../utils/clientIdGroups"
import { Item as BaseItem } from "baseui/side-navigation"
import CompanyInfo from "../public/images/sidebar/company_info.svg"
import Lock from "../public/images/sidebar/lock.svg"
import MenuBook from "../public/images/sidebar/menu_book.svg"
import Notice from "../public/images/sidebar/notice.svg"
import Payment from "../public/images/sidebar/payment.svg"
import Point from "../public/images/sidebar/point.svg"
import Shipping from "../public/images/sidebar/shipping.svg"
import Arrow from "../public/images/sidebar/sidebar_arrow.svg"
import Support from "../public/images/sidebar/support.svg"
import Package from "../public/images/sidebar/package.svg"
import CSTicket from "../public/images/sidebar/cs_ticket.svg"
import { ROLE } from "../utils/role"

export type SideItem = BaseItem & {
  activeOnSubPath?: boolean
  icon: React.FC<{ className?: string }>
  subItems?: { title: string; itemId: string }[]
}

function useSideNavItem() {
  const router = useRouter()
  const { isAuthenticated } = useAuthentication()

  const { clientId, clientRoles } = useUserInfo()
  const routerStartsWithAccount = router.pathname.startsWith("/account")
  const getClientInfoUrl = routerStartsWithAccount ? null : "/api/clients/me"

  const { data: client } = useSWRWithAuth<Client>(getClientInfoUrl)

  const { isUsingPoints } = usePoints()
  const hasIncheonTemporaryRole = clientRoles.includes("incheon:temporary")
  const hasCSTicketRole = clientRoles.includes(ROLE.csTicket)

  function sideItemsByClient(): SideItem[] {
    const isSMEClient = client?.role === "SME"

    const hasManagerClientIds =
      client &&
      client.extraProperties &&
      "managerClientIds" in client.extraProperties &&
      typeof client.extraProperties["managerClientIds"] === "object" &&
      client.extraProperties["managerClientIds"].length > 0

    const hasManagedClientIds =
      client &&
      client.extraProperties &&
      "managedClientIds" in client.extraProperties &&
      typeof client.extraProperties["managedClientIds"] === "object" &&
      client.extraProperties["managedClientIds"].length > 0

    const isUsingPackage = (() => {
      if (!client) {
        return false
      }

      if (!client?.extraProperties) {
        return false
      }

      if (hasManagerClientIds) {
        return true
      }

      if (hasManagedClientIds) {
        return true
      }

      return false
    })()

    // 로그인하지 않은 SME 사용자의 sidebar 요소
    // 렌더링 시점에 url 마지막 부분에 #error=login...이 붙어서 아래와 같이 처리
    if (
      !isAuthenticated &&
      router.asPath.startsWith("/account/create/incheon")
    ) {
      return [
        {
          title: "이용문의",
          itemId: "https://manual.amazing.today",
          icon: Support,
        },
        {
          title: "비밀번호 변경",
          itemId: "/account/reset-password",
          icon: Lock,
        },
      ]
    }

    if (hasIncheonTemporaryRole) {
      const tempIncheonItems: SideItem[] = [
        {
          title: "사업장 정보 등록",
          itemId: "/account/config/incheon",
          icon: CompanyInfo,
        },
      ]
      if (isSMEClient)
        tempIncheonItems.push({
          title: "공지사항",
          itemId: "https://manual.amazing.today/notice",
          icon: Notice,
        })

      return tempIncheonItems.concat({
        title: "이용방법",
        itemId: "https://manual.amazing.today",
        icon: MenuBook,
      })
    }

    const baseNavItems: SideItem[] = []
    const isOrdersHidden =
      !!clientId && ORDERS_HIDDEN_CLIENT_IDS.includes(clientId)
    if (isAuthenticated && !isOrdersHidden) {
      baseNavItems.push({
        title: "배송관리",
        itemId: "/orders",
        icon: Shipping,
      })
    }

    if (isAuthenticated && isUsingPackage) {
      baseNavItems.push({
        title: "패키지관리",
        itemId: "/package_order",
        icon: Package,
      })
    }

    if (isUsingPoints)
      baseNavItems.push({
        title: "포인트 충전",
        itemId: "/points",
        icon: Point,
      })
    if (client?.enableBilling)
      baseNavItems.push({
        title: "결제센터",
        itemId: "/billings",
        icon: Payment,
      })
    if (hasCSTicketRole)
      baseNavItems.push({
        title: "고객사 문의사항",
        itemId: "/cs/tickets",
        activeOnSubPath: true,
        icon: CSTicket,
        subItems: [
          { title: "전체", itemId: "/cs/tickets" },
          { title: "처리중", itemId: "/cs/tickets/in-progress" },
          { title: "종결", itemId: "/cs/tickets/closed" },
        ],
      })
    if (clientId && FASTBOX_CLIENT_IDS.includes(clientId))
      baseNavItems.push({
        title: "패스트박스",
        itemId: "/fastbox",
        icon: Arrow,
      })
    if (isSMEClient)
      baseNavItems.push({
        title: "공지사항",
        itemId: "https://manual.amazing.today/notice",
        icon: Notice,
      })
    if (!isOrdersHidden)
      baseNavItems.push({
        title: "이용방법",
        itemId: isSMEClient
          ? "https://manual.amazing.today"
          : isUsingPackage
          ? "https://vtov.notion.site/146a1491271980dcbed3d22a4f4d426a"
          : "https://manual.amazing.today/today/access",
        icon: MenuBook,
      })
    if (isSMEClient || hasManagerClientIds)
      baseNavItems.push({
        title: "비밀번호 변경",
        itemId: "/account/reset-password",
        icon: Lock,
      })

    return baseNavItems
  }

  return { client, routerStartsWithAccount, sideItemsByClient }
}

export default useSideNavItem
